/**
 * Render output dimension presets.
 *
 * The camera's render output size is expressed directly as pixel width and
 * height in the store (`renderWidth`, `renderHeight`). These presets are
 * quick-apply conveniences for common targets — they are never stored
 * themselves; applying one writes to `setRenderDimensions`.
 */

/** Minimum legal pixel value for `renderWidth` / `renderHeight`. */
export const MIN_RENDER_DIM = 16;
/** Maximum legal pixel value for `renderWidth` / `renderHeight`. */
export const MAX_RENDER_DIM = 8192;

/** Clamp and round an arbitrary numeric input to a legal render dimension. */
export function clampRenderDim(px: number): number {
  if (!Number.isFinite(px)) return MIN_RENDER_DIM;
  return Math.max(MIN_RENDER_DIM, Math.min(MAX_RENDER_DIM, Math.round(px)));
}

export interface DimensionPreset {
  /** Human-readable label shown in the dropdown. */
  label: string;
  width: number;
  height: number;
  /** Grouping for display. */
  group: "Horizontal" | "Square" | "Vertical";
}

export const DIMENSION_PRESETS: DimensionPreset[] = [
  { label: "HD 720p", width: 1280, height: 720, group: "Horizontal" },
  { label: "Full HD 1080p", width: 1920, height: 1080, group: "Horizontal" },
  { label: "QHD 1440p", width: 2560, height: 1440, group: "Horizontal" },
  { label: "4K UHD", width: 3840, height: 2160, group: "Horizontal" },
  { label: "DCI 4K", width: 4096, height: 2160, group: "Horizontal" },
  { label: "Square 1080", width: 1080, height: 1080, group: "Square" },
  { label: "Square 2048", width: 2048, height: 2048, group: "Square" },
  { label: "Portrait 1080", width: 1080, height: 1920, group: "Vertical" },
  { label: "Portrait 1440", width: 1440, height: 2560, group: "Vertical" },
];

/**
 * Simplify a width/height pair to an integer ratio with a shared divisor
 * of at most 1. Returns `null` if the ratio does not simplify cleanly
 * within a small tolerance (so callers can fall back to a decimal
 * representation for things like `1873 × 1117`).
 */
export function simplifyAspectRatio(
  width: number,
  height: number,
): { w: number; h: number } | null {
  if (width <= 0 || height <= 0) return null;
  const divisor = gcd(width, height);
  const w = width / divisor;
  const h = height / divisor;
  // Only bother showing an "integer ratio" if it's concise enough to be
  // useful. Anything larger is visual noise — the decimal form is clearer.
  if (w > 64 || h > 64) return null;
  return { w, h };
}

function gcd(a: number, b: number): number {
  a = Math.abs(Math.round(a));
  b = Math.abs(Math.round(b));
  while (b > 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a || 1;
}
